import { useEffect, useRef, useState } from 'react';
import { useBodyScrollLock } from '../useBodyScrollLock';
import { DatenschutzInfoPopover } from './DatenschutzInfoPopover';
import '../App.css';
import styles from './Modal.module.css';
import rocketImage from '../public/rocketImage.png';
import coinImage from '../public/coinImage.png';
import percentageImage from '../public/percentageImage.png';

const INITIAL_FORM = {
  studioname: '',
  email: '',
  telefon: '',
};

function Modal({ isOpen, onClose, showFormSuccess, onFormSubmit }) {
  const dialogRef = useRef(null);
  const telefonInputRef = useRef(null);
  const [slide, setSlide] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [showError, setShowError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    return () => {
      if (dialog.open) {
        dialog.close();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (showFormSuccess) {
      setSlide(2);
      return;
    }
    setSlide(0);
    setFormData(INITIAL_FORM);
    setShowError(false);
    setSubmitting(false);
    setSubmitError(null);
  }, [isOpen, showFormSuccess]);

  useBodyScrollLock(isOpen);

  const handleClose = () => {
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === dialogRef.current) {
      handleClose();
    }
  };

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (showError) setShowError(false);
    setSubmitError(null);
  };

  const handleTelefonChange = (e) => {
    let value = e.target.value;
    if (value.startsWith('+49')) {
      value = value.slice(3);
    }
    setFormData((prev) => ({ ...prev, telefon: value }));
    if (showError) setShowError(false);
    setSubmitError(null);
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const contactFormValid =
    formData.studioname.trim().length > 0 && validateEmail(formData.email.trim());

  const focusTelefonInput = () => {
    telefonInputRef.current?.focus();
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactFormValid || submitting) return;
    if (!formData.studioname.trim() || !formData.email.trim() || !validateEmail(formData.email)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    setSubmitError(null);
    setSubmitting(true);
    try {
      await onFormSubmit({
        studioname: formData.studioname,
        email: formData.email,
        telefon: formData.telefon.trim() ? `+49${formData.telefon}` : '',
      });
    } catch (err) {
      setSubmitError(err?.message || 'Senden fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const privacyFooter = (
    <>
      <p className={styles.spamNote}>Kein Spam. Versprochen.</p>
      <DatenschutzInfoPopover
        triggerClassName={`datenschutzLink slimRegularSmallText ${styles.modalPrivacyLink}`}
      />
    </>
  );

  return (
    <dialog
      ref={dialogRef}
      className={styles.modal}
      onClick={handleBackdropClick}
      onClose={handleClose}
    >
      <div className={styles.modalWrapper}>
        <div className={styles.modalContent} onClick={handleContentClick}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Schließen"
          >
            ×
          </button>

          {slide === 0 && (
            <div className={styles.slideLayout}>
              <header className={styles.fixedHeader}>
                <h2 className={styles.benefitsTitle}>
                  Das Listing auf Stu ist kostenlos und unverbindlich.
                </h2>
                <p className={styles.benefitsSubtitle}>
                  Werde eines der ersten 100 Studios <br/> und sichere dir:
                </p>
              </header>
              <div className={styles.scrollBody}>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitText}>
                    <h3>Mehr Sichtbarkeit</h3>
                    <p>Als Stu Original wirst du weiter oben gelistet!</p>
                  </div>
                  <div className={styles.benefitIcon}>
                    <img src={rocketImage} alt="" />
                  </div>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitText}>
                    <h3>Ein exklusives Badge</h3>
                    <p>Für alle, die von Anfang an dabei sind!</p>
                  </div>
                  <div className={styles.benefitIcon}>
                    <img src={coinImage} alt="" />
                  </div>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitText}>
                    <h3>Reduzierte Provision</h3>
                    <p>Ein Jahr lang 5% statt 15% Provisionsgebühr.</p>
                  </div>
                  <div className={styles.benefitIcon}>
                    <img src={percentageImage} alt="" />
                  </div>
                </div>
              </div>
              <footer className={styles.fixedFooter}>
                <button
                  type="button"
                  className={styles.btnPrimaryDark}
                  onClick={() => setSlide(1)}
                >
                  Kostenlos entdecken
                </button>
              </footer>
            </div>
          )}

          {slide === 1 && (
            <form className={styles.slideLayout} onSubmit={handleContactSubmit} noValidate>
              <header className={styles.fixedHeader}>
                <h2 className={styles.benefitsTitle2}>
                  Bitte teile uns deine Kontaktdaten mit, damit wir dich erreichen können.
                </h2>
              </header>
              <div className={styles.scrollBody}>
                <div className={styles.field}>
                  <label htmlFor="modal-studioname">Studioname*</label>
                  <input
                    id="modal-studioname"
                    name="studioname"
                    className={styles.input}
                    type="text"
                    placeholder="Studioname eingeben"
                    value={formData.studioname}
                    onChange={handleChange}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="modal-email">E-Mail*</label>
                  <input
                    id="modal-email"
                    name="email"
                    className={styles.input}
                    type="email"
                    placeholder="E-Mail eingeben"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="modal-telefon" className={styles.telefonLabel}>
                    <span>Telefonnummer</span>{' '}
                    <span className={styles.labelOptional}>(optional)</span>
                  </label>
                  <div
                    className={styles.phoneDualRow}
                    onClick={focusTelefonInput}
                    role="presentation"
                  >
                    <span className={styles.phonePrefixBox}>+49</span>
                    <input
                      ref={telefonInputRef}
                      id="modal-telefon"
                      name="telefon"
                      className={styles.phoneInputGlued}
                      type="tel"
                      placeholder="Telefonnummer eingeben"
                      value={formData.telefon}
                      onChange={handleTelefonChange}
                    />
                  </div>
                </div>
                {showError && (
                  <p className={styles.fieldError} role="alert">
                    Bitte Studioname und gültige E-Mail ausfüllen.
                  </p>
                )}
                {submitError && (
                  <p className={styles.fieldError} role="alert">
                    {submitError}
                  </p>
                )}
              </div>
              <footer className={styles.fixedFooter}>
                <button
                  type="submit"
                  className={`joinButton ${contactFormValid ? 'valid' : ''} ${styles.submitLikeHome}`}
                  disabled={!contactFormValid || submitting}
                >
                  {submitting ? 'Wird gesendet…' : 'Abschicken'}
                </button>
                {privacyFooter}
              </footer>
            </form>
          )}

          {slide === 2 && (
            <div className={styles.slideLayout}>
              <div className={`${styles.scrollBody} ${styles.successBody}`}>
                <div className={styles.successIcon} aria-hidden="true">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 className={styles.successTitle}>Deine Anfrage wurde erfolgreich versendet!</h2>
                <p className={styles.successSubtitle}>Wir werden dich schnellstmöglich kontaktieren.</p>
              </div>
              <footer className={styles.fixedFooter}>
                <button
                  type="button"
                  className={styles.btnPrimaryPink}
                  onClick={handleClose}
                >
                  Fertig
                </button>
                {privacyFooter}
              </footer>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}

export default Modal;
